'use strict';

import uuid from 'uuid';
import store from 'store';
import moment from 'moment';
import indexBy from 'lodash.indexby';
import { fetchDocumentsById } from './Content';
import { flattenIngredients, getFoodIdsFromIngredients } from './Recipe';
import { getConfig } from './Env';
import { isSunbasketFood } from './Sunbasket';
import { roundToDecimals } from './Math';
import { getBaseServings } from './servingsCalculations';

const mealNames = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

export function isScalable(primary){
    return primary?.content?.servings && (primary?.content?.type !== "food" || isSunbasketFood(primary?.food));
}

export function dateCompare(a, b) {
    let aDate = moment(a.date);
    let bDate = moment(b.date);

    if (bDate.isBefore(a.date)) {
        return 1;
    }

    if (aDate.isBefore(b.date)) {
        return -1;
    }

    return 0;
}

export function mealSortByCreated(a, b) {
    return new Date(b.created) - new Date(a.created);
}

/**
 * Normalizes participants field to string format for comparison operations
 * Handles both string and array formats for consistent processing
 * @param {string|Array} participants - Participants as string "uuid1,uuid2" or array ['uuid1', 'uuid2'] or [{uuid, portion}]
 * @returns {string} Normalized participants string in comma-separated format
 */
function normalizeParticipantsToString(participants) {
    // Already a string - return as-is
    if (typeof participants === 'string') {
        return participants;
    }

    // Array format (can be array of strings or array of objects)
    if (Array.isArray(participants)) {
        return participants
            .map(p => {
                // If object (e.g., {uuid, portion}), extract uuid property
                if (typeof p === 'object' && p !== null) {
                    return p.uuid;
                }
                // If primitive (string), use as-is
                return p;
            })
            .filter(p => p !== null && p !== undefined) // Remove null/undefined but keep empty strings (guests)
            .join(',');
    }

    // null/undefined/other - return empty string
    return '';
}

/**
 * Normalizes participants field to array format for processing
 * Handles string CSV, arrays, and null/undefined consistently
 * IMPORTANT: Uses typeof check to properly handle empty strings (empty string represents 1 guest)
 *
 * @param {string|Array|null|undefined} participants - Participants in various formats:
 *   - String CSV: "uuid1,uuid2" or "" (empty string = 1 guest) or "," (2 guests)
 *   - Array: ['uuid1', 'uuid2'] or [] or ['']
 *   - null/undefined: treated as no participants
 * @returns {Array<string>} Array of participant UUIDs
 *
 * @example
 * normalizeParticipantsToArray("USER,member-1") // ["USER", "member-1"]
 * normalizeParticipantsToArray("") // [""] - represents 1 guest
 * normalizeParticipantsToArray(",") // ["", ""] - represents 2 guests
 * normalizeParticipantsToArray(["USER"]) // ["USER"]
 * normalizeParticipantsToArray(null) // []
 */
export function normalizeParticipantsToArray(participants) {
    // String format (CSV) - split by comma
    // IMPORTANT: Use typeof check instead of truthy check
    // because empty string "" is falsy but valid (represents 1 guest)
    if (typeof participants === 'string') {
        return participants.split(',');
    }

    // Already an array - return as-is
    if (Array.isArray(participants)) {
        return participants;
    }

    // null/undefined/other - return empty array
    return [];
}


/** Enriches meals with pre-calculated sorting flags for better performance
 * @param {Array} meals - Array of meal objects
 * @param {Object} recipes - Optional recipes lookup object
 * @param {Object} details - Optional details lookup object
 * @param {Object} foods - Optional foods lookup object (for store bought items)
 * @returns {Array} Enriched meals with _isDessert flag
 */
export function enrichMealsForSorting(meals, recipes = {}, details = {}, foods = {}) {
    return meals.map(meal => {
        // Ensure we get explicit boolean (true/false) not undefined
        const isDessert = !!(
            meal.tags?.includes('Dessert') ||
            recipes[meal.recipe_uuid]?.tags?.includes('Dessert') ||
            details[meal.details_uuid]?.tags?.includes('Dessert') ||
            foods[meal.food_uuid]?.tags?.includes('Dessert')
        );

        return {
            ...meal,
            _isDessert: isDessert
        };
    });
}

export function mealSortCompare(a, b) {
    // #0 sort by deleted go last
    if (!a.deleted && b.deleted) return -1;
    if (a.deleted && !b.deleted) return 1;

    // #1 Sort by Date
    const aDate = moment(a.date),
        bDate = moment(b.date);
    if (!aDate.isSame(bDate, 'day')) {
        return aDate - bDate;
    }

    // #2 Sort by meal type
    // TODO: Check with Andrew about eating patterns
    let ai = mealNames.indexOf(a.meal),
        bi = mealNames.indexOf(b.meal);

    if (ai != -1 && bi == -1) return -1;
    if (ai == -1 && bi != -1) return 1;

    if (ai > bi) return 1;
    if (ai < bi) return -1;

    // #3 Sort main dishes > side dishes > desserts
    // Use pre-calculated _isDessert flag (set by enrichMealsForSorting)
    const aIsDessert = a._isDessert || false;
    const bIsDessert = b._isDessert || false;

    // Desserts come last
    if (aIsDessert && !bIsDessert) return 1;
    if (!aIsDessert && bIsDessert) return -1;

    // Side dishes come after main dishes
    if (a.side_dish && !b.side_dish) return 1;
    if (!a.side_dish && b.side_dish) return -1;

    // #3 - fresh > *
    if (a.meal_type === 'fresh' && b.meal_type !== 'fresh') return -1;
    if (a.meal_type !== 'fresh' && b.meal_type === 'fresh') return 1;

    // #4 - leftovers > *(except fresh)
    if (a.meal_type === 'leftover' && b.meal_type !== 'leftover') return -1;
    if (a.meal_type !== 'leftover' && b.meal_type === 'leftover') return 1;

    // #5 Sunbasket foods come before other foods. Only sunbasket foods should have participants
    if (a.meal_type === 'food' && b.meal_type === 'food') {
        // Normalize participants to string format (handles both string and array formats)
        const aParticipants = normalizeParticipantsToString(a.participants);
        const bParticipants = normalizeParticipantsToString(b.participants);

        const aHasParticipants = aParticipants.trim() !== '';
        const bHasParticipants = bParticipants.trim() !== '';

        if (aHasParticipants && !bHasParticipants) {
            return -1;
        }
        if (!aHasParticipants && bHasParticipants) {
            return 1;
        }
    }

    // #6 - Tiebreaker: sort by id/uuid for deterministic ordering
    // This ensures multi-dish meals always appear in the same order
    const aId = a.id || a.uuid || '';
    const bId = b.id || b.uuid || '';
    if (aId < bId) return -1;
    if (aId > bId) return 1;

    return 0;
}

export function updateMealLeftovers(meal, allMeals, recipe, profile, leftoverDates = []) {
    let dirtyMeals = [meal];

    if (!profile.preferences.leftovers_enabled) {
        return { dirtyMeals, toRemove: [], leftovers: [] };
    }

    // If the meal is already a leftover, we have nothing else to do.
    if (!['food', 'fresh'].includes(meal.meal_type)) {
        return { dirtyMeals, toRemove: [], leftovers: [] };
    }

    let tags = (recipe && recipe.tags) || [];

    // Do we have a no leftovers tag? Don't bother creating leftovers records.
    if (tags.indexOf('No Leftovers') != -1) {
        return { dirtyMeals, toRemove: [], leftovers: [] };
    }

    // Find all leftovers records, sort by earliest to latest
    let oldLeftovers = allMeals.filter((m) => m.parent_uuid == meal.uuid).sort(dateCompare);

    // If no leftover dates provided but we have old leftovers, remove them all
    if (!leftoverDates.length) {
        return { dirtyMeals, toRemove: oldLeftovers, leftovers: [] };
    }

    const leftovers = [];

    leftoverDates.forEach(({ leftoverDate, mealType: leftoverMealType, participants: leftoverParticipants }) => {
        let leftover;

        if (oldLeftovers.length) {
            leftover = Object.assign({}, oldLeftovers.shift());
            leftover.date = leftoverDate.format('YYYY-MM-DD');
            leftover.meal = leftoverMealType;
            leftover.participants = leftoverParticipants.map(p => p.uuid).join(",");
            leftover.scaling = meal.scaling;
            leftover.deleted = false;
        } else {
            leftover = {
                ...meal,
                uuid: uuid.v4(),
                // A leftover is eaten later: it never copies the logged portion of its main meal
                logged_portion: null,
                meal_type: 'leftover',
                date: leftoverDate.format('YYYY-MM-DD'),
                parent_uuid: meal.uuid,
                meal: leftoverMealType,
                participants: leftoverParticipants.map(p => p.uuid).join(","),
            };
        }

        leftovers.push(leftover);
        dirtyMeals.push(leftover);
    });

    return { dirtyMeals, toRemove: oldLeftovers, leftovers };
}

export function createMealsForPlan(plan, dateStart, user, planMarker) {
    let meals = [];
    let created = moment().format();

    // This will make sure first meals of the plan will always start on the day that
    // the user picked to start the plan.
    let minOffset = 999;

    plan.items.forEach((item) => {
        if (item.offset < minOffset) {
            minOffset = item.offset;
        }
    });

    if (minOffset > 50) {
        minOffset = 0;
    }

    // Get a list of all the fresh meals, and then a list of all the leftovers. We'll do the fresh meals first
    const fresh = plan.items.filter((item) => item.meal_type === 'fresh'),
        leftovers = plan.items.filter((item) => item.meal_type === 'leftover'),
        rest = plan.items.filter((item) => !['fresh', 'leftover'].includes(item.meal_type));

    const freshIndex = {};
    const mealTypes = [];

    fresh
        .concat(leftovers)
        .concat(rest)
        .forEach((item) => {
            const {
                meal_type = 'fresh',
                scaling = 1,
                participants,
                recipe_uuid,
                details_uuid,
                food_uuid,
                ...rest
            } = item;

            // Ensure participants is always an array with at least 'USER'
            const mealParticipants = participants && Array.isArray(participants) && participants.length > 0
                ? participants
                : ['USER'];

            let meal = {
                ...rest,
                uuid: uuid.v4(),
                created,
                meal_type,
                recipe_uuid,
                details_uuid,
                food_uuid,
                scaling,
                participants: mealParticipants.join(','),
                meal: item.meal,
                date: moment(dateStart)
                    .add(item.offset - minOffset, 'day')
                    .format('YYYY-MM-DD'),
                plan_uuid: planMarker.uuid,
                plan_date: moment(dateStart).format('YYYY-MM-DD'),
            };

            if (item.meal_type === 'fresh' && item.id) {
                freshIndex[item.id] = meal;
            }

            if (item.meal_type === 'leftover' && freshIndex[item.parent]) {
                meal.parent_uuid = freshIndex[item.parent].uuid;
            }

            if (!mealTypes.includes(meal.meal)) {
                mealTypes.push(meal.meal);
            }

            meals.push(meal);
        });

    // Loop through all the meals again and pull out the maximum date
    let maxDate = moment.max(meals.map((m) => moment(m.date)));

    return { meals, maxDate, mealTypes };
}

export function getAssetsForMeals(meals, extraUuids = []) {
    return new Promise((resolve) => {
        // 1. Load list of top-level recipe and details for these meals
        // 2. Load list of subrecipes & subdetails for these meals (merge with recipe & details lists)
        // 3. Load list of all foods for these meals

        // extract a list of details uuids, subrecipe and subdetails IDs.
        let firstUuids = [];

        // Guard against a missing/non-array `meals` argument. A malformed or
        // partially-loaded plan should resolve to empty assets rather than throw
        // ("undefined is not an object (evaluating 'e.forEach')") and blank the
        // whole meal feed. See EATLOVE-646 / EAT-4058.
        (Array.isArray(meals) ? meals : []).forEach((m) => {
            const type = m.type || m.meal_type;

            if ((type === 'leftover' || type === 'fresh') && m.recipe_uuid) {
                firstUuids.push(m.recipe_uuid);
                firstUuids.push(m.details_uuid);
                return;
            }

            if (m.food_uuid) firstUuids.push(m.food_uuid);
            if (m.location_uuid) firstUuids.push(m.location_uuid);
        });

        if (extraUuids && extraUuids.length) {
            firstUuids = firstUuids.concat(extraUuids);
        }

        // First load the details
        fetchDocumentsById(firstUuids).then((documents) => {
            let recipes = indexBy(
                documents.filter((d) => d && d.type == 'recipe'),
                'uuid'
            );
            let details = indexBy(
                documents.filter((d) => d && d.type == 'recipe_details'),
                'uuid'
            );
            let foods = indexBy(
                documents.filter((d) => d && d.type == 'food'),
                'uuid'
            );
            let locations = indexBy(
                documents.filter((d) => d && d.type == 'brand_location'),
                'uuid'
            );

            // Load all subrecipe and subdetails uuids
            let secondUuids = [];
            Object.keys(recipes).forEach((id) => {
                if (recipes[id] && recipes[id].merchant && recipes[id].merchant.uuid) {
                    secondUuids.push(recipes[id].merchant.uuid);
                }

                if (!recipes[id].subrecipes) {
                    return;
                }

                recipes[id].subrecipes.forEach((sr) => {
                    secondUuids.push(sr.recipe);
                    secondUuids.push(sr.details);
                });
            });

            Object.keys(foods).forEach((id) => {
                if (foods[id].brand_uuid) {
                    secondUuids.push(foods[id].brand_uuid);
                }
            });

            fetchDocumentsById(secondUuids).then((documents) => {
                let subrecipes = indexBy(
                    documents.filter((d) => d && d.type == 'recipe'),
                    'uuid'
                );
                let subdetails = indexBy(
                    documents.filter((d) => d && d.type == 'recipe_details'),
                    'uuid'
                );
                let merchants = indexBy(
                    documents.filter((d) => d && d.type == 'merchant'),
                    'uuid'
                );
                let brands = indexBy(
                    documents.filter((d) => d && d.type == 'brand'),
                    'uuid'
                );

                recipes = { ...recipes, ...subrecipes };
                details = { ...details, ...subdetails };

                // Flatten all ingredients from all detail records, we want to include
                // subrecipes ingredients in this ingredients list as well.
                let ingredients = flattenIngredients(details);

                // Load foods too, rendering needs that as well.
                let ingredientIds = getFoodIdsFromIngredients(ingredients);

                fetchDocumentsById(ingredientIds).then((documents) => {
                    foods = {
                        ...foods,
                        ...indexBy(
                            documents.filter((d) => d && d.type === 'food'),
                            'uuid'
                        ),
                    };
                    subrecipes = indexBy(
                        documents.filter((d) => d && d.type == 'recipe'),
                        'uuid'
                    );
                    subdetails = indexBy(
                        documents.filter((d) => d && d.type == 'recipe_details'),
                        'uuid'
                    );

                    return resolve({
                        recipes,
                        details,
                        foods,
                        merchants,
                        subrecipes,
                        subdetails,
                        brands,
                        locations,
                        content: {
                            ...recipes,
                            ...details,
                            ...foods,
                            ...merchants,
                            ...subrecipes,
                            ...subdetails,
                            ...brands,
                            ...locations,
                        },
                    });
                });
            });
        });
    });
}

export function getRecipesForMeals(meals) {
    const uuids = [];

    // Deduplicate and map in one step
    meals.forEach((meal) => {
        if (uuids.indexOf(meal.recipe_uuid) == -1) {
            uuids.push(meal.recipe_uuid);
        }
    });

    return fetchDocumentsById(uuids);
}

export function hasMealsTurnedOn(profile) {
    return (profile.preferences.meal_types || []).some(
        (mealType) => !mealType.participants || mealType.participants.length > 0,
    );
}

export function getParticipantsForProfileByMealType(profile, mealTypeName, includeProfileAlways = false) {
    // Accept either mealTypeName, or object mealType
    const mealType = typeof mealTypeName === 'string'
        ? profile.preferences.meal_types.find((mt) => mt.name === mealTypeName)
        : mealTypeName;

    if(!mealType) {
        return [];
    }

    const familyIndex = indexBy(profile.family, 'uuid');

    mealType.participants = mealType.participants || [];

    const user = {
        uuid: 'USER',
        name: profile.name || profile.first_name || 'USER',
        portion: profile.portion
    }

    let participants = mealType.participants.map(participantId => {
        if (participantId === 'USER') {
            return user;
        }

        return familyIndex[participantId];
    })

    // Filter out undefined/null values first, before checking length
    participants = participants.filter(p => p);

    if (includeProfileAlways && !participants.find(p => p.uuid === 'USER')) {
        participants.push(user);
    }

    // If no valid participants were found, default to the primary user
    if (participants.length === 0) {
        participants.push(user);
    }

    return participants;
}

export function getParticipantsInfo(user) {
    const userName = user.name || user.first_name || 'USER';
    const family = user.family || [];
    const participantDetails = [{uuid: 'USER', name: userName, portion: user.portion}].concat(family);

    return participantDetails;
}

/**
 * Builds the full list of everyone participating in a plan for display purposes:
 * the primary user, all family members, and any guests added to individual meals.
 *
 * Guests are stored per-meal as empty-uuid participants (see EditMealServingsModal),
 * so they have no stable identity across the plan. We surface them by taking the
 * maximum number of guests appearing on any single meal — mirroring how the servings
 * modal counts guests for a slot.
 *
 * @param {Object} profile - The plan profile (primary user + family + preferences)
 * @param {Object} [plan] - The plan; guests are derived from plan.items
 * @returns {Array<Object>} Participant descriptors tagged with `participantType`
 *   ('primary' | 'family' | 'guest')
 */
export function getAllPlanParticipants(profile, plan) {
    if (!profile) {
        return [];
    }

    const fullName = (person) =>
        person.name || `${person.first_name || ''} ${person.last_name || ''}`.trim();

    const primary = {
        ...profile,
        uuid: 'USER',
        name: fullName(profile) || 'USER',
        portion: profile.portion,
        participantType: 'primary',
    };
    // profile carries the whole plan (boards/items); don't drag those into a participant descriptor
    delete primary.boards;
    delete primary.family;

    const family = (profile.family || []).map(member => ({
        ...member,
        name: fullName(member) || 'Unnamed',
        participantType: 'family',
    }));

    // Guests: max count seen on any single, non-deleted meal
    let maxGuests = 0;
    (plan?.items || []).forEach(meal => {
        if (meal.deleted) {
            return;
        }
        const guestCount = normalizeParticipantsToArray(meal.participants)
            .filter(uuid => !uuid || uuid === '' || (typeof uuid === 'string' && uuid.startsWith('guest-')))
            .length;
        if (guestCount > maxGuests) {
            maxGuests = guestCount;
        }
    });

    const guests = Array.from({ length: maxGuests }, (unused, i) => ({
        uuid: `guest-${i + 1}`,
        name: 'Guest #' + (i + 1),
        portion: 1,
        participantType: 'guest',
    }));

    return [primary, ...family, ...guests];
}

const BUCKET_LABELS = { adult: 'Adult', preteen: 'Preteen', child: 'Child' };

// Age thresholds match the Pro family editor's getAgeRange; no birthdate falls through to adult.
function ageBucket(birthdate) {
    const age = birthdate ? moment().diff(moment(birthdate), 'years') : NaN;

    if (age < 7) {
        return 'child';
    }

    if (age < 13) {
        return 'preteen';
    }

    return 'adult';
}

// Rename a stored favourite's participants to generic labels ("Adult #1", "Child #1", ...)
// for the no-patient-in-context view, keeping uuid/portion/type so plan items still resolve.
export function genericizeProfileParticipants(profile) {
    if (!profile) {
        return profile;
    }

    // A shallow copy is enough: we only delete top-level fields from the copy and
    // rebuild `family` with `map`. The profile can carry `boards` and plan items, so a
    // deep clone would copy a lot for a few renamed fields.
    const generic = { ...profile };
    const counts = { adult: 0, preteen: 0, child: 0 };

    const label = (bucket) => {
        counts[bucket] += 1;
        return `${BUCKET_LABELS[bucket]} #${counts[bucket]}`;
    };

    const stripIdentity = (person) => {
        delete person.first_name;
        delete person.last_name;
        delete person.birthdate;
        delete person.gender;
    };

    // The primary user has no `type`, so their bucket comes from birthdate.
    generic.name = label(ageBucket(generic.birthdate));
    stripIdentity(generic);

    generic.family = (generic.family || []).map((member) => {
        const bucket = BUCKET_LABELS[member.type] ? member.type : 'adult';
        const cleaned = { ...member, name: label(bucket) };
        stripIdentity(cleaned);

        return cleaned;
    });

    return generic;
}

export function getParticipantsForSpecificMeal(meal, profile) {
    if (!profile) {
        return [];
    }

    if (!meal.participants || meal.participants.length === 0) {
        return [];
    }

    const participantsInfo = getParticipantsInfo(profile);
    const mealParticipants = normalizeParticipantsToArray(meal.participants);

    let guestNo = 1;
    return mealParticipants
        .map((uuid) => {
            const member = participantsInfo.find(p => p.uuid === uuid);

            if (member) {
                return member;
            }

            if (!uuid || uuid === '' || uuid.startsWith('guest-')) {
                return { uuid, name: 'Guest #' + guestNo++, portion: 1 };
            }

            return null;
        })
        .filter(p => p !== null); // Remove invalid participants
}

export function getParticipantsForMeal(meals, meal, profile) {

    if (!profile) {
        return null;
    }

    if (!meal.participants) {
        const name = profile.name ? profile.name : profile.first_name;
        return [{ uuid: 'USER', name, portion: 1 }];
    }

    let guestNo = 1;
    const mealAndLeftovers = getMealAndLeftovers(meal, meals);

    const participants = mealAndLeftovers.flatMap(m => normalizeParticipantsToArray(m.participants));

    return participants?.map((uuid) => {
        if (uuid === 'USER') {
            const name = profile.name ? profile.name : profile.first_name;
            return { uuid, name, portion: profile.portion };
        }

        // Find the uuid in the family array, use that
        let member = profile.family.filter((m) => m.uuid === uuid)[0];
        if (member) {
            return member;
        }

        if (!uuid || uuid === '' || uuid.startsWith('guest-')) {
            return { uuid, name: 'Guest #' + guestNo++, portion: 1 };
        }

        return null;
    }).filter(p => p !== null);
}


/**
 * Calculates the default participants for the current profile on the current recipe. This tries
 * to figure out if its a breakfast lunch and dinner and appropriately add those family members to the meal
 * @param  {[type]} recipe  [description]
 * @param  {[type]} profile [description]
 * @return {[type]}         [description]
 */
export function getDefaultParticipantsForRecipe(content, profile) {
    // if the profile is not set, we don't have any participants still
    if (!profile) {
        return null;
    }

    // Return all participants (USER + all family members) for meal search flow
    // This is used when opening recipe details from meal search (not add swap context)
    // Since users can now have multiple dishes of same type in one meal slot,
    // we can't filter by recipe tags anymore
    return getParticipantsInfo(profile);
}

export function isParticipating(participants, participant, mealType = null) {
    // Guard against participant being null, and the meal type being invalid
    if (!(participant && participant.uuid)) {
        return false;
    }

    if (mealType && !(participants && participants[mealType])) {
        return false;
    }

    if (!mealType && !participants) {
        return false;
    }

    const result = (mealType ? participants[mealType] : participants).filter((p) => p.uuid === participant.uuid);

    return result.length > 0;
}

// The dishes of a slot that can be eaten (and logged): not notes, not deleted
export function getEdibleMeals(meals) {
    return meals.filter((m) => ['fresh', 'leftover', 'food'].includes(m?.meal_type) && !m.deleted);
}

export function areMealsLogged(meals) {
    const edibleMeals = getEdibleMeals(meals);

    return edibleMeals.length > 0 && edibleMeals.every((m) => m.logged_portion);
}

// The recipe and food uuids that a meal-level favorite adds and removes: one per loaded,
// non-deleted dish of the slot, without repeats (a fresh dish and its leftover share a recipe)
export function getMealFavoriteUuids(meals, recipes = {}, foods = {}) {
    const uuids = meals
        .filter((m) => ['fresh', 'leftover', 'food'].includes(m?.meal_type) && !m.deleted)
        .map((m) => {
            if (m.recipe_uuid && recipes[m.recipe_uuid]) {
                return m.recipe_uuid;
            }

            return m.food_uuid && foods[m.food_uuid] ? m.food_uuid : null;
        })
        .filter(Boolean);

    return [...new Set(uuids)];
}

export function getPrimaryMeal(meals, recipes = {}, foods = {}, debug = false, alreadySorted = false) {
    if (!(meals && meals.length > 0)) {
        return { titles: [], photos: [], others: [], dishes: 0, beverages: 0 };
    }

    let edibleMeals = meals.filter((m) => ['fresh', 'leftover', 'food'].includes(m?.meal_type));

    // Only sort if not already sorted
    if (edibleMeals.length > 1 && !alreadySorted) {
        // Enrich meals with sorting flags, then sort
        edibleMeals = enrichMealsForSorting(edibleMeals, recipes, {}, foods);
        edibleMeals.sort(mealSortCompare);
    }

    const primary = edibleMeals[0];

    let dishes = 0,
        beverages = 0;

    const others = edibleMeals
        .map((m) => {
            if (m.deleted) {
                return;
            }

            let content = null;

            if (m.recipe_uuid && recipes && recipes[m.recipe_uuid]) {
                content = recipes[m.recipe_uuid];
            }

            if (m.food_uuid && foods && foods[m.food_uuid]) {
                content = foods[m.food_uuid];
            }

            if (content && (content.tags || []).includes('Beverage')) {
                beverages++;
            } else {
                dishes++;
            }

            return content;
        })
        .filter((v) => v);

    const recipe = primary && primary.recipe_uuid && recipes ? recipes[primary.recipe_uuid] : null,
        food = primary && primary.food_uuid && foods ? foods[primary.food_uuid] : null;

    return {
        primary,
        recipe,
        food,
        dishes,
        beverages,
        content: recipe || food,
        others,
        titles: others.map((o) => {
            if (o.type === 'food') {
                return o.pretty_name || o.name;
            }

            return o.title;
        }),
        deletedTitles: edibleMeals
            .filter((m) => m.deleted)
            .map((m) => {
                let c = recipes[m?.recipe_uuid] || foods[m?.food_uuid];

                return c?.title || c?.pretty_name || c?.name;
            }),
        photos: aggregateMealPhotos(edibleMeals, recipes, foods),
    };
}

export function aggregateMealPhotos(meals, recipes, foods, daily_log = false) {
    const photos = [];

    const self_host = getConfig('self_host');

    // Enrich meals if not using daily_log sorting
    if (!daily_log) {
        meals = enrichMealsForSorting(meals, recipes, {}, foods);
    }

    meals.sort(daily_log ? mealSortByCreated : mealSortCompare).forEach((meal) => {
        if (meal.deleted) {
            return;
        }

        const type = meal.meal_type || meal.type; // allow for scheduled or meal plan meal items.

        if (
            (type === 'fresh' || type === 'leftover') &&
            recipes &&
            recipes[meal.recipe_uuid] &&
            recipes[meal.recipe_uuid].image
        ) {
            photos.push({ url: recipes[meal.recipe_uuid].image, logged_portion: meal.logged_portion });
        }

        if (
            (type === 'fresh' || type === 'leftover') &&
            foods &&
            foods[meal.food_uuid] &&
            foods[meal.food_uuid].image
        ) {
            photos.push({ url: foods[meal.food_uuid].image, logged_portion: meal.logged_portion });
        }

        if (type === 'food' && foods && foods[meal.food_uuid] && foods[meal.food_uuid].image) {
            photos.push({ url: foods[meal.food_uuid].image, logged_portion: meal.logged_portion });
        }

        if (meal.image_uuids && meal.image_uuids.length) {
            meal.image_uuids.forEach((uuid) => {
                photos.push({
                    url: 'https://' + self_host + '/file-proxy/' + uuid + '?type=image',
                    isDeletable: true,
                    logged_portion: meal.logged_portion,
                });
            });
        }
    });

    // ensure that we get the best image
    photos.sort((a, b) => {
        if (a.isDeletable && !b.isDeletable) return -1;
        if (!a.isDeletable && b.isDeletable) return 1;

        if (a.isDeletable && b.isDeletable) {
            return photos.indexOf(b) - photos.indexOf(a);
        }

        return 0;
    });

    return photos;
}

export function getStartTimeForMeal(startTimeInMinutes, format = 'h:mm a', hideZeroMinutes = false) {
    const midnightTime = moment('00:00', 'HH:mm');
    const currentTime = midnightTime.add(startTimeInMinutes, 'minutes');
    const formattedCurrentTime =
        startTimeInMinutes % 60 === 0 && hideZeroMinutes ? currentTime.format('h a') : currentTime.format(format);
    return formattedCurrentTime;
}

export function getMealSlotsFromMeals(meals) {
    let collector = {};

    meals.forEach((meal) => {
        const key = meal.date + meal.meal;

        collector[key] = collector[key] || { date: meal.date, mealType: meal.meal };
    });

    return Object.keys(collector).map((key) => collector[key]);
}

export function getContentForMeal(meal, contents) {
    let content = null;

    if (meal.deleted) {
        return;
    }

    if (meal.recipe_uuid) content = contents[meal.recipe_uuid];
    if (meal.food_uuid) content = contents[meal.food_uuid];

    return content;
}

export function getTagsForMeals(meals, contents) {
    let tags = [];

    meals.forEach((meal) => {
        let content = getContentForMeal(meal, contents);

        if (!content) {
            return;
        }

        tags = tags.concat((content && content.tags) || []);
    });

    tags = tags.filter((tag, i) => tags.indexOf(tag) === i);

    return tags;
}

export function getIngredientTagsForMeals(meals, contents) {
    let tags = [];

    meals.forEach((meal) => {
        let content = getContentForMeal(meal, contents);

        if (!content) {
            return;
        }

        tags = tags.concat((content && content.ingredient_tags) || []);
    });

    tags = tags.filter((tag, i) => tags.indexOf(tag) === i);

    return tags;
}

export const isDateAvailable = (date, food) => {
    const availableOn = getDateAvailables(food).map((d) => d.format('YYYY-MM-DD'));

    date = moment(date);

    const needDeliveryOn = [
        date.format('YYYY-MM-DD'),
        date.subtract(1, 'day').format('YYYY-MM-DD'),
        date.subtract(1, 'day').format('YYYY-MM-DD'),
        date.subtract(1, 'day').format('YYYY-MM-DD'),
        date.subtract(1, 'day').format('YYYY-MM-DD'),
    ];

    // If there is an intersection between availableOn and needDeliveryOn,
    // then we can successfully deliver this food on that date.
    return needDeliveryOn.filter((d) => availableOn.includes(d)).length > 0;
};

export const getDateAvailables = (food) => {
    if (!food) {
        return [];
    }

    if (Array.isArray(food)) {
        if (food.length > 1) {
            return [];
        }
        food = food[0];
    }

    if (!food.source_dates_available) {
        return [];
    }

    return food.source_dates_available.map((p) => moment(p));
};

export const getMealInfo = (primary, recipes, foods) => {
    let mealName = null;
    let productType = null;
    let brandName = null;

    if (primary) {
        if ((primary.meal_type === 'fresh' || primary.meal_type === 'leftover') && recipes[primary.recipe_uuid]) {
            const recipe = recipes[primary.recipe_uuid];
            mealName = primary.meal_type === 'leftover' ? 'Leftover - ' + recipe.title : recipe.title;
            productType = 'recipe';
        } else if (primary.meal_type === 'food' && foods[primary.food_uuid]) {
            const food = foods[primary.food_uuid];
            mealName = food.name;
            brandName = food.brand_name;
            productType = food.product_type;
        } else {
            mealName = '';
            productType = primary.meal_type;
        }
    }

    return {
        mealName: mealName,
        productType: productType,
        brandName: brandName,
    };
};


export const getMaxCostPerServingForMealType = (mealType, preferences) => {
    const { meal_types = [] } = preferences || {};

    const mealTypeObj = meal_types.find((mt) => mt.name === mealType || mt.main_dish === mealType);
    const maxCostPerServingForMealType = mealTypeObj?.max_cost_per_serving;

    return maxCostPerServingForMealType ? { lte: maxCostPerServingForMealType } : undefined;
};

// ======================================================================
// DEPRECATED: Grocery quantity calculation functions moved to Grocery.js
// ======================================================================
// The following functions have been moved to Grocery.js with better names:
// - getMealsByGrocery → Moved to Grocery.js
// - getPortionByParticipants → Moved to Grocery.js
// - calculateQuantity → Renamed to calculateGroceryPackageQuantity in Grocery.js
// - calculateQuantityByNewMeals → Renamed to calculateGroceryPackageQuantityWithNewMeals in Grocery.js
// - recalculateQuantityByGrocery → Removed (was unused)
//
// Import from '../utils/Grocery' instead:
// import { calculateGroceryPackageQuantityWithNewMeals } from '../utils/Grocery';
// ======================================================================

export function getGramsByPackage(packageName, food) {
    const unitGrams = food.units?.find((pkg) => pkg.description === packageName);
    const pkgGrams = food.packaging?.find((pkg) => pkg.description === packageName);

    if (!!unitGrams) {
        return unitGrams?.grams;
    }

    if (!!food.packaging && pkgGrams) {
        return pkgGrams?.grams;
    }

    return 1;
}

export const getRepeatOverlaps = (
    mealsToRepeat,
    selection,
    uuidsToRepeat,
    ogLeftoverDates,
    allMeals,
    recipes,
    foods,
    user
) => {
    const overlaps = [],
        clears = [];

    mealsToRepeat = mealsToRepeat?.filter((p) => !p.deleted) || [];

    selection.forEach(({ date, mealType }) => {
        const sourceMeals = mealsToRepeat.map((m) => ({
            ...m,
            uuid: uuid.v4(), // Give every meal a new uuid
            logged_portion: null,
        }));

        const isDestMeal = (m) =>
            !m.deleted && m.meal === mealType && date.isSame(m.date, 'day') && !uuidsToRepeat.includes(m.parent_uuid);

        const destMeals = allMeals.filter(isDestMeal);

        if (destMeals.length) {
            overlaps.push({
                mealType,
                date,
                destMeals,
                sourceMeals,
            });
        } else {
            clears.push({
                mealType,
                date,
                sourceMeals,
            });
        }

        const { primary } = getPrimaryMeal(sourceMeals, recipes, foods);

        if (primary.meal_type === 'fresh') {
            const participants = getParticipantsForMeal(allMeals, primary, user);
            const neededPerMeal = participants.reduce((total, member) => total + (member.portion || 1), 0);

            // How many days of leftovers did mealsToRepeat have?
            ogLeftoverDates.forEach((dummy, i) => {
                const leftoverDate = moment(date).add(i + 1, 'day');

                const isDestMeal = (m) =>
                    !m.deleted &&
                    m.meal === mealType &&
                    leftoverDate.isSame(m.date, 'day') &&
                    !uuidsToRepeat.includes(m.parent_uuid);
                const destMeals = allMeals.filter(isDestMeal);

                // Create a leftover record for each sourceMeal
                const sourceLeftovers = sourceMeals.map((sourceMeal) => ({
                    ...sourceMeal,
                    uuid: uuid.v4(),
                    meal_type: 'leftover',
                    parent_uuid: sourceMeal.uuid,
                    leftover_servings: neededPerMeal,
                }));

                if (destMeals.length) {
                    overlaps.push({
                        mealType,
                        date: leftoverDate,
                        sourceMeals: sourceLeftovers,
                        destMeals,
                    });
                } else {
                    clears.push({
                        mealType,
                        date: leftoverDate,
                        sourceMeals: sourceLeftovers,
                    });
                }
            });
        }
    });

    return { overlaps, clears };
};

export function getServingsNeededWithLoggedServings(participants, loggedServings) {
    if (!participants || !Array.isArray(participants)) {
        return loggedServings || 0;
    }

    return participants.reduce(
        (total, member) => total + (loggedServings && member.uuid == 'USER' ? loggedServings : member.portion),
        0
    );
}

export function getLoggedServingsOfRecipe(meal, recipe, roundToNearestServing = true) {
    function roundUpToThreeDecimalPoints(num) {
        return Math.round(num * 1000) / 1000;
    }

    function customRound(quotient, remainder, threshold = 5) {
        if (remainder > threshold) {
            return Math.ceil(quotient);
        } else {
            return Math.floor(quotient);
        }
    }

    if (meal?.logged_unit == 'serving' && meal?.logged_amount !== undefined) {
        return roundToDecimals(meal.logged_amount, 2);
    }

    if (recipe?.grams_per_serving && meal?.logged_grams) {
        const servingSize = roundUpToThreeDecimalPoints(recipe.grams_per_serving);
        const quotient = meal.logged_grams / servingSize;
        const remainder = meal.logged_grams % servingSize;
        return roundToNearestServing ? customRound(quotient, remainder) : quotient;
    }

    if (recipe?.milliliters_per_serving && meal?.logged_milliliters) {
        const servingSize = roundUpToThreeDecimalPoints(recipe.milliliters_per_serving);
        const quotient = meal.logged_milliliters / servingSize;
        const remainder = meal.logged_milliliters % servingSize;
        return roundToNearestServing ? customRound(quotient, remainder) : quotient;
    }

    if (meal?.logged_amount !== undefined) {
        return roundToDecimals(meal.logged_amount, 2);
    }
    return meal?.logged_amount;
}

export function generateLeftovers(user, meals, date, mealType, participants, totalDays) {
    const existingMeal = meals.find(meal => meal.meal === mealType && date.isSame(meal.date, 'day'));

    let existingMealLeftovers = [];
    if (existingMeal) {
        existingMealLeftovers = meals.filter((m) => m.parent_uuid == existingMeal.uuid).sort(dateCompare);
    }

    const totalLeftovers = totalDays ? totalDays : existingMealLeftovers.length;

    const leftovers = [];

    for (let i = 0; i < totalLeftovers && leftovers.length < 5; i++) {
        const leftoverDate = existingMealLeftovers[i] ? moment(existingMealLeftovers[i].date, 'YYYY-MM-DD') : moment(date).add(i + 1, 'days');
        const leftoverMealType = existingMealLeftovers[i] ? existingMealLeftovers[i].meal : mealType;
        const leftoverParticipants = existingMealLeftovers[i] ? existingMealLeftovers[i].participants : participants;

        const participantDetails = getParticipantsInfo(user);
        let leftoverParticipantsInfo;

        if (typeof leftoverParticipants === 'string') {
            // Case: comma-separated string "USER,FAMILY_MEMBER_1" or "" (guest)
            const participantUuids = normalizeParticipantsToArray(leftoverParticipants);
            leftoverParticipantsInfo = participantUuids.map(uuid => {
                const member = participantDetails.find(p => p.uuid === uuid);
                if (member) {
                    return member;
                }
                return { uuid: '', name: 'Guest', portion: 1 };
            });
        } else if (Array.isArray(leftoverParticipants) && leftoverParticipants.length > 0 && typeof leftoverParticipants[0] === 'object') {
            // Case: array of participant objects [{uuid: "USER", name: "...", portion: 1}]
            leftoverParticipantsInfo = leftoverParticipants;
        } else if (Array.isArray(leftoverParticipants)) {
            // Case: array of UUID strings ["USER", "FAMILY_MEMBER_1"]
            leftoverParticipantsInfo = participantDetails.filter(p => leftoverParticipants.includes(p.uuid));
        } else {
            leftoverParticipantsInfo = [];
        }

        leftovers.push({leftoverDate, mealType: leftoverMealType, participants: leftoverParticipantsInfo});
    }

    return leftovers;
}

const normalizeParticipants = (participants) => {
    if (!participants || participants.length === 0) {
        return [];
    }

    if (Array.isArray(participants) && typeof participants[0] === 'string') {
        return participants.map((uuid) => ({ uuid }));
    }

    if (typeof participants === 'string') {
        return participants.split(',').map((uuid) => ({ uuid: uuid.trim() }));
    }

    return participants;
};

export function areParticipantsDifferent(newParticipants, oldParticipants) {
    const normalizedNewParticipants = normalizeParticipants(newParticipants);
    const normalizedOldParticipants = normalizeParticipants(oldParticipants);

    const uuids1 = normalizedNewParticipants.map((obj) => obj.uuid);
    const uuids2 = normalizedOldParticipants.map((obj) => obj.uuid);

    if (uuids1.length !== uuids2.length) {
        return true;
    }

    // Check if all UUIDs in uuids1 are present in uuids2 and vice-versa
    return !uuids1.every((uuid) => uuids2.includes(uuid)) || !uuids2.every((uuid) => uuids1.includes(uuid));
}

export function getMealAndLeftovers(meal, meals) {
    let parent = null;
    let allMeals = [];

    if (meal.meal_type == 'leftover') {
        parent = meals.find((item) => item.uuid === meal.parent_uuid);
    } else {
        parent = meal;
    }

    allMeals = meals.filter((item) => item.parent_uuid === parent?.uuid && !item.deleted);

    if (parent) {
        allMeals.unshift(parent);
    }

    allMeals.sort((a, b) => mealSortCompare(a, b));

    return allMeals;
}

/**
 * Retrieves batch information for a given meal, including participants, servings needed, total days and leftover meals.
 *
 * @param {string} date - The date of the meal.
 * @param {string} mealType - The type of the meal.
 * @param {Object} profile - The user profile, used to get participants for the meal.
 * @param {Array} meals - An array of meal objects.
 *
 * @returns {Object|null} An object, or null if no meal is found.
 * @returns {Array} return.participants - An array of participants for the meal.
 * @returns {number} return.mealServingsNeeded - Total number of servings needed for the meal.
 * @returns {number} return.totalDays - The total number of days covered by the meal and leftovers.
 * @returns {Array} return.leftoverDates - Dates of leftover meals.
 */
export function getBatchInfo(date, mealType, profile, meals) {
    const alreadyScheduledMeal = meals.find(
        (meal) => meal.meal === mealType && meal.date === date && meal.meal_type !== 'leftover' && !meal.deleted
    );

    if (alreadyScheduledMeal) {
        const allMeals = getMealAndLeftovers(alreadyScheduledMeal, meals);

        // Sum servings needed across ALL meals (main + leftovers) individually
        // Each meal can have different participants, so we cannot multiply by totalDays
        let mealServingsNeeded = 0;
        allMeals.forEach(meal => {
            const mealParticipants = getParticipantsForSpecificMeal(meal, profile);
            mealServingsNeeded += mealParticipants.reduce((sum, p) => sum + p.portion, 0);
        });

        // Get participants for display (use main meal participants)
        const participants = getParticipantsForMeal(meals, alreadyScheduledMeal, profile);

        return { participants, mealServingsNeeded };
    }

    return null;
}

export function recalculateScaling(dish, participants, recipe, leftovers = [], meals, profile = null, extraServings = 0) {
    // If this is a food (not a recipe), scaling is always 1
    // Exception: Sunbasket foods are scalable
    const isSunbasket = recipe && isSunbasketFood(recipe);
    if (((dish && dish.meal_type === 'food') || (recipe && recipe.type === 'food')) && !isSunbasket) {
        return 1;
    }

    // If no recipe is provided, return scaling of 1
    if (!recipe) {
        return 1;
    }

    // If participants is not provided or empty for a recipe, return default scaling
    if (!participants || !Array.isArray(participants) || participants.length === 0) {
        return 1;
    }

    const servings = recipe?.servings || 1;

    let totalNeeded = 0;
    let parent;

    if (dish) {
        // Find the parent/main meal
        if (dish.meal_type == 'leftover') {
            parent = meals.find((item) => item.uuid === dish.parent_uuid || item.id === dish.parent);
        } else {
            parent = dish;
        }

        // Calculate MAIN MEAL need - use correct participants based on what's being edited
        let mainMealParticipants;
        const isEditingMainMeal = (dish.uuid && dish.uuid === parent.uuid) || (dish.id && dish.id === parent.id);
        if (isEditingMainMeal) {
            // Editing main meal - use NEW participants from modal
            mainMealParticipants = participants;
        } else {
            // Editing leftover - use main meal's EXISTING participants (single meal only!)
            mainMealParticipants = profile ? getParticipantsForSpecificMeal(parent, profile) : participants;
        }

        const mainMealLogged = getLoggedServingsOfRecipe(parent, recipe);
        const mainMealNeed = getServingsNeededWithLoggedServings(mainMealParticipants, mainMealLogged);
        totalNeeded += mainMealNeed;

        // Calculate needs for EACH LEFTOVER (existing + pending)
        const leftoverNeeds = [];
        leftovers.forEach((leftover) => {
            // Try to find existing leftover in meals array
            const existingLeftover = leftover.uuid != null
                ? meals.find(m => m.uuid === leftover.uuid || m.id === leftover.uuid)
                : undefined;
            const leftoverLogged = existingLeftover
                ? getLoggedServingsOfRecipe(existingLeftover, recipe)
                : mainMealLogged; // Pending leftovers use parent's logged amount

            // Get participants array - leftovers array should already have correct participants
            let leftoverParticipants;
            if (Array.isArray(leftover.participants) && leftover.participants.length > 0) {
                // Participants already provided in leftover object (from wrapper or modal)
                leftoverParticipants = leftover.participants;
            } else if (existingLeftover && profile) {
                // Fallback: get from existing leftover in database
                leftoverParticipants = getParticipantsForSpecificMeal(existingLeftover, profile);
            } else {
                // Last resort: use main meal participants
                leftoverParticipants = participants;
            }

            const leftoverNeed = getServingsNeededWithLoggedServings(leftoverParticipants, leftoverLogged);
            leftoverNeeds.push(leftoverNeed);
            totalNeeded += leftoverNeed;
        });
    } else {
        // No dish provided, just calculate from participants
        totalNeeded = getServingsNeededWithLoggedServings(participants, null);
    }

    let scaling = Math.ceil((totalNeeded + extraServings) / servings);
    return scaling;
}

export function getRescheduleOverlaps (user, allMeals, recipes, foods, plans, mealsToReschedule, date, mealType, replaceMeals = [], userLeftovers = null) {
    let overlaps = [], clears = [];

    const { primary } = getPrimaryMeal(mealsToReschedule, recipes, foods);

    const uuidsToReschedule = mealsToReschedule.map(m => m.uuid);
    const isLeftover = !mealsToReschedule.some(m => m.meal_type !== 'leftover');
    const replaceMealIds = (replaceMeals || []).map(m => m.uuid);

    // Are there meals at the destination?
    const destMeals = allMeals.filter(m => !m.deleted &&
                                        m.meal === mealType &&
                                        date.isSame(m.date, 'day') &&
                                        !uuidsToReschedule.includes(m.uuid) &&
                                        !uuidsToReschedule.includes(m.parent_uuid) &&
                                        !replaceMealIds.includes(m.uuid) &&
                                        !replaceMealIds.includes(m.parent_id));

    if (destMeals.length) {
        overlaps.push({
            mealType,
            date,
            destMeals,
            sourceMeals: mealsToReschedule,
        });
    } else {
        clears.push({
            mealType,
            date,
            sourceMeals: mealsToReschedule,
        });
    }
    const mealAndLeftovers = getMealAndLeftovers(primary, allMeals);

    const mealTypesForDate = getMealTypesForDate(date, plans, user.preferences.meal_types);
    const destinationMealTypeIndex = mealTypesForDate.findIndex((mt) => mt.name === mealType);
    const leftOverMealIndex = mealTypesForDate.findIndex((mt) => mt.name === mealAndLeftovers[1]?.meal);

    const isBeforeLeftovers = mealAndLeftovers.length > 1 && (
        (date.isBefore(mealAndLeftovers[1].date, 'day') ||
        (date.isSame(mealAndLeftovers[1].date, 'day') && destinationMealTypeIndex < leftOverMealIndex))
    );

    if (isBeforeLeftovers || isLeftover || mealAndLeftovers.length === 1) {
        return { mealsToReschedule, overlaps, clears };
    } else if (mealAndLeftovers.length > 1 && !userLeftovers) {
        return {
            primary,
            mealsToReschedule,
            showLeftOverOffestModal: true,
            overlaps,  // Main meal destination conflicts (from lines 1388-1393)
            clears     // Main meal destination clears (from lines 1395-1399)
        };
    } else if (userLeftovers) {
        userLeftovers.forEach(({leftoverDate, mealType, participants}, index) => {

            // Find the meals at the destination that would be overwritten.
            const destMeals = allMeals.filter(m => m.meal === mealType &&
                                                leftoverDate.isSame(m.date, 'day') &&
                                                !uuidsToReschedule.includes(m.uuid) &&
                                                !uuidsToReschedule.includes(m.parent_uuid));

            let sourceMeals = [];

            // Process all meals to reschedule, not just primary meal
            mealsToReschedule.forEach(meal => {
                const mealAndItsLeftovers = getMealAndLeftovers(meal, allMeals);
                const leftoverForThisMeal = mealAndItsLeftovers[index + 1];

                if (leftoverForThisMeal) {
                    sourceMeals.push(leftoverForThisMeal);
                }
            });

            // If there are already meals at our destination?
            if (destMeals.length && sourceMeals.length) {
                overlaps.push({
                    date: leftoverDate,
                    mealType,
                    participants,
                    destMeals,
                    sourceMeals,
                });
            } else  {
                clears.push({
                    mealType,
                    participants,
                    date: leftoverDate,
                    sourceMeals,
                });
            }
        });

        return { mealsToReschedule, overlaps, clears }
    }
}

/**
 * Determines if leftover modal should be shown for a meal
 *
 * @param {Object} content - Recipe or food object
 * @param {number} scaling - Recipe scaling factor
 * @param {Array} participants - Participant objects with portions
 * @param {Object} profile - User profile with preferences
 * @param {number} loggedServings - Optional logged servings (for existing meals)
 * @returns {boolean} True if leftovers should be offered
 */
export function shouldCreateLeftovers(content, scaling, participants, profile, loggedServings = null) {
    // Check 1: Leftovers enabled in preferences
    if (!profile?.preferences?.leftovers_enabled) {
        return false;
    }

    // Check 2: Content has "No Leftovers" tag
    const tags = content?.tags || [];
    if (tags.indexOf('No Leftovers') !== -1) {
        return false;
    }

    // Check 3: Content must have valid servings (scalable content)
    // Check raw servings value before getBaseServings fallback kicks in
    if (!content?.servings || content.servings <= 0) {
        return false;
    }

    const servings = getBaseServings(content);

    // Check 4: Must have excess servings
    const needed = getServingsNeededWithLoggedServings(participants, loggedServings);
    const totalServings = servings * (scaling || 1);

    const hasExcess = totalServings > needed;

    return hasExcess;
}

// ================================== Meal Types ==================================

/**
 * Gets complete information for a meal type
 * @param {string|object} mealTypeName - Meal type name or meal type object
 * @param {moment|string} date - Date for temporal context
 * @param {Array} mealTypes - Array of meal types from preferences.meal_types or plans.meal_types
 * @param {Array} plans - Nutrition plans with specific periods
 * @returns {object|null} Complete meal type information
 */
export function getMealTypeInfoByName(mealTypeName, date, mealTypes, plans) {
    if (typeof mealTypeName === 'object') {
        return mealTypeName; // If mealTypeName is already an object, return it directly
    }

    // First try meal types (should have implicit values)
    let mealTypeInfo = mealTypes?.find((mt) => mt.name === mealTypeName);

    // If not found, search all matching plans in most-recently-created order
    if (!mealTypeInfo) {
        const sortedMatchingPlans = (plans || [])
            .filter(p => p?.date_start && p?.date_end && date && moment(date).isBetween(p.date_start, p.date_end, 'day', '[]'))
            .sort((a, b) => moment(b.created).valueOf() - moment(a.created).valueOf());

        for (const matchingPlan of sortedMatchingPlans) {
            mealTypeInfo = (matchingPlan?.profile_key?.meal_types || []).find(mt => mt.name === mealTypeName);
            if (mealTypeInfo) break;
        }

        // Fall back to defaultMealTypes (user prefs) if not found in any plan
        if (!mealTypeInfo) {
            mealTypeInfo = (mealTypes || []).find(mt => mt.name === mealTypeName);
        }

        // If found in plan but missing implicit values, use envelope from user prefs meal types
        if (mealTypeInfo && mealTypeInfo.envelope) {
            const hasImplicit = Object.values(mealTypeInfo.envelope).some(
                env => env.implicit && Object.keys(env.implicit).length > 0
            );

            if (!hasImplicit) {
                // Find best match in user prefs meal types by main_dish tag
                const fallbackMealType = (mealTypes || []).find(
                    mt => mt.main_dish === mealTypeInfo.main_dish
                );

                if (fallbackMealType?.envelope) {
                    // Deep clone to avoid side effects
                    mealTypeInfo = JSON.parse(JSON.stringify(mealTypeInfo));
                    mealTypeInfo.envelope = fallbackMealType.envelope;
                }
            }
        }
    }

    return mealTypeInfo;
}

// Canonical meal-type defaults — ported from
// users/migrations/129-eating-patterns/data/meal-types.php  (begin/end = minutes from midnight)
const DEFAULT_MEAL_TYPES = [
    { name: 'Breakfast',     main_dish: 'Breakfast', side_dish: 'Breakfast Side Dish', begin: 480, end: 600,  pick_seq: 2 },
    { name: 'Morning Snack', main_dish: 'Snack',                                        begin: 600, end: 690,  pick_seq: 4 },
    { name: 'Lunch',         main_dish: 'Lunch',     side_dish: 'Lunch Side Dish',      begin: 690, end: 840,  pick_seq: 1 },
    { name: 'Dinner',        main_dish: 'Dinner',    side_dish: 'Side Dish',            begin: 960, end: 1080, pick_seq: 0 },
];

/**
 * Resolve meal types for an old plan (no plan.meal_types) by deriving the list from the
 * plan's own items, then enriching each by name from the profile, falling back to defaults.
 *
 * @param {Object} plan - Meal plan (may carry items[] but no meal_types)
 * @param {Object} profile - User/patient profile with preferences.meal_types
 * @returns {Array} Meal type objects, ordered chronologically by begin
 */
function deriveMealTypesFromItems(plan, profile) {
    const profileIndex = indexBy(profile?.preferences?.meal_types || [], 'name');
    const defaultIndex = indexBy(DEFAULT_MEAL_TYPES, 'name');

    const names = [...new Set((plan?.items || []).map(item => item.meal).filter(Boolean))];
    const list = names.length ? names : DEFAULT_MEAL_TYPES.map(d => d.name); // empty plan -> B/S/L/D

    return list
        .map(name => profileIndex[name] || defaultIndex[name] || { name })
        .sort((a, b) => (a.begin ?? Infinity) - (b.begin ?? Infinity));
}

/**
 * Single source of truth for the meal types a plan follows.
 * 1) plan.meal_types alone, if present (new structure)
 * 2) otherwise derive from plan items, matching the profile, then canonical defaults
 *
 * @param {Object} plan - Meal plan
 * @param {Object} profile - User/patient profile
 * @returns {Array} Meal type objects
 */
export function getPlanMealTypes(plan, profile) {
    if (Array.isArray(plan?.meal_types) && plan.meal_types.length) {
        return plan.meal_types;
    }

    return deriveMealTypesFromItems(plan, profile);
}

/**
 * Get meal types for a specific date, considering eating patterns (plans) or falling back to user preferences
 * @param {moment} date - The date to get meal types for
 * @param {Array} plans - Array of nutrition plans with date ranges and meal types
 * @param {Array} defaultMealTypes - Default meal types from user preferences
 * @returns {Array} Array of meal type objects for the specified date
 */
export function getMealTypesForDate(date, plans, defaultMealTypes = []) {
    const matching = (plans || []).filter((plan) => {
        if (!plan || !plan.date_start || !plan.date_end) return false;
        return moment(date).isBetween(plan.date_start, plan.date_end, 'day', '[]');
    });

    // Most recently created plan takes precedence when date ranges overlap
    matching.sort((a, b) => moment(b.created).valueOf() - moment(a.created).valueOf());
    const plan = matching[0];

    return plan?.profile_key?.meal_types || defaultMealTypes;
}

/**
 * Finds the currently active meal type
 * @param {Object} preferences - User preferences containing meal_types
 * @param {Array} plans - Nutrition plans
 * @returns {string} Current meal type name
 */
export function getCurrentActiveMealType(preferences, plans) {
    const now = moment();
    const mealTypes = getMealTypesForDate(now, plans, preferences?.meal_types);

    // Store validation - keep the intelligence we already have
    const explicit = store.get('meal-feed-explicit-meal');
    if (explicit && mealTypes?.some(mt => mt.name === explicit)) {
        return explicit;
    }

    // Your moment at midnight
    const midnight = moment().startOf('day');

    // Difference in minutes
    const sinceMidnight = now.diff(midnight, 'minutes');

    // First check if we're currently in any meal's active time window
    const currentMealType = mealTypes.find(
        (mealType) => sinceMidnight >= mealType.begin && sinceMidnight <= mealType.end
    );

    if (currentMealType) {
        return currentMealType.name;
    }

    // If not in any active window, find which meal period has ended most recently
    // and return the NEXT meal (when a meal's time has passed, next meal is current)

    // Sort meals by end time to find which one ended most recently
    const sortedByEndTime = [...mealTypes].sort((a, b) => a.end - b.end);

    // Find the most recent meal that has already ended
    let mostRecentEndedMeal = null;
    for (const meal of sortedByEndTime) {
        if (sinceMidnight > meal.end) {
            mostRecentEndedMeal = meal;
        } else {
            break;
        }
    }

    if (mostRecentEndedMeal) {
        // Find the next meal after the one that ended
        const sortedByBeginTime = [...mealTypes].sort((a, b) => a.begin - b.begin);
        const endedMealIndex = sortedByBeginTime.findIndex(m => m.name === mostRecentEndedMeal.name);

        if (endedMealIndex !== -1 && endedMealIndex < sortedByBeginTime.length - 1) {
            // Return the next meal in sequence
            return sortedByBeginTime[endedMealIndex + 1].name;
        } else {
            // If it was the last meal, it remains current until end of day
            return mostRecentEndedMeal.name;
        }
    }

    // If no meals have ended yet (early morning), return the first meal of the day
    const firstMeal = [...mealTypes].sort((a, b) => a.begin - b.begin)[0];
    return firstMeal?.name || '';
}

/**
 * Determines the mode/status of a meal based on current time and meal configurations
 * Uses meal intervals (begin/end) with gaps between meals, consistent with getCurrentActiveMealType
 * @param {moment|string} date - The date of the meal
 * @param {string} mealType - The type of meal (e.g., 'Breakfast', 'Lunch')
 * @param {object} preferences - User preferences containing meal_types
 * @param {Array} plans - Nutrition plans
 * @param {string} currentMealType - The currently active meal type
 * @param {Array} allMealTypes - Array of all meal type names
 * @returns {string} 'past', 'current', or 'future'
 */
export function getMealMode(date, mealType, preferences, plans, currentMealType, allMealTypes) {
    const now = moment();

    // If it's a future day, everything is future
    if (now.isBefore(date, 'day')) {
        return 'future';
    }
    // If it's a past day, everything is past
    else if (now.isAfter(date, 'day')) {
        return 'past';
    }
    // If it's today, check based on meal times and current active meal
    else {
        // The currentMealType parameter already comes from getCurrentActiveMealType()
        // which handles the logic of gaps and determining the active meal

        // If the requested meal is the current active meal, it's current
        if (mealType === currentMealType) {
            return 'current';
        }

        // Get meal types configuration for the date
        const mealTypes = getMealTypesForDate(date, plans, preferences?.meal_types);
        const midnight = moment().startOf('day');
        const sinceMidnight = now.diff(midnight, 'minutes');

        // Find the requested meal configuration
        const requestedMeal = mealTypes.find(mt => mt.name === mealType);
        const currentMeal = mealTypes.find(mt => mt.name === currentMealType);

        // If configurations not found, use fallback logic with meal order
        if (!requestedMeal || !currentMeal) {
            const defaultMealIndex = allMealTypes.indexOf(currentMealType);
            const requestedMealIndex = allMealTypes.indexOf(mealType);

            if (requestedMealIndex > defaultMealIndex) {
                return 'future';
            } else {
                return 'past';
            }
        }

        // Check if the requested meal has already ended
        if (sinceMidnight > requestedMeal.end) {
            return 'past';
        }

        // Check if the requested meal hasn't started yet
        // But we need to be careful with gaps - if we're in a gap and the meal is the "next" meal
        // (which would be currentMealType), it should be current, not future
        if (sinceMidnight < requestedMeal.begin && mealType !== currentMealType) {
            return 'future';
        }

        // For any edge cases, compare with current meal's begin time
        if (requestedMeal.begin < currentMeal.begin) {
            return 'past';
        } else if (requestedMeal.begin > currentMeal.begin) {
            return 'future';
        } else {
            // Same begin time as current meal
            return 'current';
        }
    }
}

// ================================== End Meal Types ==================================
